import { useMutation, useQueryClient } from '@tanstack/react-query';

import { reportingLeLensPath } from '~/consts/reportingLeLensApi';

import { request } from '~/services/common';

import type { LensResponse } from '~/types/leLens';

import { resolveReportingLeLensUrl } from '~/utils/resolveReportingLeLensUrl';

import { encodedJsonBody } from './encodedJsonBody';
import { reportingLeLensQueryKeys } from './useGetReportingLeLens';
import { refreshCertifiedShelves } from './useLensCertification';
import { personalViewQueryKeys } from './usePersonalViews';

export type LensPublicationInput =
  | { action: 'start' | 'takeover' | 'publish' | 'discard'; baseVersion: number }
  | {
      action: 'saveAs';
      baseVersion: number;
      lensVersion: number;
      title: string;
      audience: 'private' | 'source';
      spaceId: string | null;
    };

export default function useLensPublication(lensId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationKey: ['lens-publication', lensId],
    onMutate: () => client.cancelQueries({ queryKey: reportingLeLensQueryKeys.detail(lensId), exact: true }),
    mutationFn: async (input: LensPublicationInput) =>
      (await request(resolveReportingLeLensUrl(`${reportingLeLensPath(lensId)}/publication`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: encodedJsonBody(input),
      })) as LensResponse,
    onSuccess: async (response, input) => {
      client.setQueryData(reportingLeLensQueryKeys.detail(response.lens.id), response);
      await client.invalidateQueries({ queryKey: reportingLeLensQueryKeys.lists() });
      if (input.action === 'publish') await client.invalidateQueries({ queryKey: personalViewQueryKeys.all });
      if (input.action === 'publish' || input.action === 'discard') {
        await client.invalidateQueries({ queryKey: [...reportingLeLensQueryKeys.detail(lensId), 'data'] });
        await refreshCertifiedShelves(client);
      }
    },
    onError: async () => {
      await client.invalidateQueries({ queryKey: reportingLeLensQueryKeys.detail(lensId), exact: true });
    },
    retry: false,
  });
}
